/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ActivityIndicator, Platform, StyleSheet, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useSessionStore } from '../session/store/useSessionStore';
import { useSessionEmployeeRecord } from '../../auth/hooks/useSessionEmployeeRecord';
import { BranchSelectionScreen } from './screen-registry';
import { PostLoginHubScreen } from '../../post-login-hub/screens/PostLoginHubScreen';
import { HubAccountScreen } from '../../post-login-hub/screens/HubAccountScreen';
import { HubOrganizationScreen } from '../../post-login-hub/screens/HubOrganizationScreen';
import {
  resolveInitialRootRouteName,
} from '../session/routing/resolvePostLoginRoute';
import { ManageBranchesOverlayScreen } from '../../settings/screens/ManageBranchesScreen';
import { ClosedBusinessScreen } from '../../settings/screens/ClosedBusinessScreen';
import { NoBusinessAccessScreen } from '../../settings/screens/NoBusinessAccessScreen';
import { ticketColors } from '../../shared/theme/ticketColors';
import { AppShellLayout } from './AppShellLayout';
import { isWebBranchSwitchOverlay } from '../../shared/layout/breakpoints';
import { shellSceneContentStyle } from './navigationTheme';
import { getSharedAppHeaderOptions } from './AppNavigatorHeaders';
import type { PlanEntitlementsRow } from '../feature-flags/domain/planEntitlements';
import type { GlobalFeatureFlagsMap } from '../feature-flags/domain/globalFeatureFlags';

const RootStack = createNativeStackNavigator();

type AppNavigatorProps = {
  entitlements: PlanEntitlementsRow | null;
  globalFeatureFlags: GlobalFeatureFlagsMap | null;
  /** From App shell (same module as AccountSetupProvider) — avoid hook here so wizard HMR cannot split context. */
  requiresOwnerOnboarding: boolean | null;
  /** ISO timestamp when the org was closed by the owner, or null if active. */
  organizationClosedAt: string | null;
};

const modalScreenOptions = {
  headerShown: true as const,
  presentation: 'modal' as const,
  headerStyle: { backgroundColor: ticketColors.counter },
  headerTintColor: ticketColors.ink,
};

/** Deferred so OrganizationSetupWizard stays out of AppNavigator's eager import graph. */
function BranchSetupScreenRoute(props: {
  onComplete?: () => void | Promise<void>;
  asCreateBranch?: boolean;
  route?: { params?: { mode?: string } };
}) {
  const { BranchSetupScreen } = require('../../auth/screens/OnboardingScreen') as typeof import('../../auth/screens/OnboardingScreen');
  const asAdditionalOrganization = props.route?.params?.mode === 'additional_organization';
  return (
    <BranchSetupScreen
      onComplete={props.onComplete}
      asCreateBranch={props.asCreateBranch}
      asAdditionalOrganization={asAdditionalOrganization}
    />
  );
}

export const AppNavigator = ({
  entitlements,
  globalFeatureFlags,
  requiresOwnerOnboarding,
  organizationClosedAt,
}: AppNavigatorProps) => {
  const session = useAuthStore((state) => state.session);
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const branches = useSessionStore((state) => state.branches);
  const employeeState = useSessionEmployeeRecord();
  const employeeRecord = employeeState.record;
  const { width: windowWidth } = useWindowDimensions();
  const activeBranchCount = branches.filter((b) => b.isActive !== false).length;
  // Escape hatch a Perfil solo si no hay sucursales asignadas (logout).
  // Con sucursales, el header / Más permiten “Elegir sucursal”.
  const showProfileEscapeHatch = !currentBranchId && branches.length === 0;

  if (!session) {
    return null;
  }

  const hasOrg = Boolean(entitlements?.organization_id);
  const hasOrgAndBranches = hasOrg && (entitlements?.branch_count ?? 0) > 0;

  // Sin organización: no mostrar BranchSelector. Si el onboarding aún no se
  // resolvió (null) o está pendiente (true), mostrar spinner para que el
  // compositor de App.tsx re-evalúe y monte el wizard.
  if (!hasOrg && requiresOwnerOnboarding !== false) {
    return (
      <View style={styles.blocked}>
        <ActivityIndicator color={ticketColors.gold} />
      </View>
    );
  }

  // Cuentas que ya tienen organización/sucursales no deberían quedar bloqueadas
  // solo porque `owner_setup_completed_at` sea null (usuarios creados antes del wizard).
  if (requiresOwnerOnboarding === true && !hasOrgAndBranches) {
    return (
      <View style={styles.blocked}>
        <ActivityIndicator color={ticketColors.gold} />
      </View>
    );
  }

  // A1: Owner con negocio cerrado → ClosedBusinessScreen.
  if (organizationClosedAt && entitlements?.is_owner === true) {
    return <ClosedBusinessScreen />;
  }

  // A4: Empleado revocado (is_active=false) → NoBusinessAccessScreen.
  if (employeeRecord?.isEmployee === true && employeeRecord.isActive === false) {
    return <NoBusinessAccessScreen />;
  }

  const isOwner = entitlements?.is_owner === true;
  const isEmployee = employeeRecord?.isEmployee === true;
  const isGrant = employeeRecord?.isGrant === true;
  const employeeRolePending =
    !isOwner && employeeState.status === 'loading' && activeBranchCount >= 2 && !currentBranchId;

  if (employeeRolePending) {
    return (
      <View style={styles.blocked}>
        <ActivityIndicator color={ticketColors.gold} />
      </View>
    );
  }

  const initialRouteName = resolveInitialRootRouteName({
    activeBranchCount,
    isOwner,
    isEmployee,
    isGrant,
    currentBranchId,
  });

  const branchSwitchMode = Boolean(currentBranchId);
  const branchSwitchOverlay = isWebBranchSwitchOverlay(windowWidth, branchSwitchMode);

  return (
    <RootStack.Navigator
      initialRouteName={initialRouteName}
      screenOptions={{ headerShown: false, contentStyle: shellSceneContentStyle }}
    >
      <RootStack.Screen name="AppShell" options={{ headerShown: false }}>
        {() => <AppShellLayout entitlements={entitlements} globalFeatureFlags={globalFeatureFlags} />}
      </RootStack.Screen>
      <RootStack.Screen
        name="PostLoginHub"
        component={PostLoginHubScreen}
        options={{
          headerShown: false,
          presentation: 'card',
          contentStyle: shellSceneContentStyle,
        }}
      />
      <RootStack.Screen
        name="HubAccount"
        component={HubAccountScreen}
        options={{
          ...modalScreenOptions,
          title: 'Mi cuenta',
          headerBackTitle: 'Panel',
        }}
      />
      <RootStack.Screen
        name="HubOrganization"
        component={HubOrganizationScreen}
        options={{
          ...modalScreenOptions,
          title: 'Organización',
          headerBackTitle: 'Panel',
        }}
      />
      <RootStack.Screen
        name="BranchSelector"
        component={BranchSelectionScreen}
        options={({ navigation }) => ({
          ...getSharedAppHeaderOptions(),
          headerShown: !branchSwitchMode,
          // Con sucursal activa: overlay transparente (sheet mobile / dialog web desktop).
          // Gate sin sucursal: card full-screen bloqueante.
          presentation: branchSwitchMode ? 'transparentModal' : 'card',
          animation: branchSwitchOverlay ? 'fade' : branchSwitchMode ? 'slide_from_bottom' : undefined,
          contentStyle: branchSwitchMode ? { backgroundColor: 'transparent' } : shellSceneContentStyle,
          gestureEnabled: branchSwitchMode,
          title: branchSwitchMode ? 'Cambiar sucursal' : 'Elegí sucursal',
          headerStyle: { backgroundColor: ticketColors.paper },
          headerTintColor: ticketColors.ink,
          headerBackVisible: false,
          headerRight: () => {
            if (showProfileEscapeHatch) {
              return (
                <TouchableOpacity
                  onPress={() =>
                    navigation.navigate('AppShell', {
                      screen: 'MainTabs',
                      params: { screen: 'Profile' },
                    })
                  }
                  accessibilityRole="button"
                  accessibilityLabel="Abrir cuenta"
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  style={styles.profileButton}
                >
                  <Ionicons name="person-circle-outline" size={26} color={ticketColors.ink} />
                </TouchableOpacity>
              );
            }
            return undefined;
          },
        })}
      />
      <RootStack.Screen name="Onboarding" component={BranchSetupScreenRoute} options={{ ...modalScreenOptions, title: 'Configuración inicial' }} />
      <RootStack.Screen
        name="ManageBranchesOverlay"
        component={ManageBranchesOverlayScreen}
        options={{
          headerShown: false,
          presentation: 'transparentModal',
          animation: branchSwitchOverlay ? 'fade' : 'slide_from_bottom',
          contentStyle: { backgroundColor: 'transparent' },
          gestureEnabled: true,
        }}
      />
      <RootStack.Screen
        name="CreateBranch"
        options={{
          headerShown: false,
          presentation: 'transparentModal',
          animation: branchSwitchOverlay ? 'fade' : 'slide_from_bottom',
          contentStyle: { backgroundColor: 'transparent' },
          gestureEnabled: true,
        }}
      >
        {() => <BranchSetupScreenRoute asCreateBranch />}
      </RootStack.Screen>
    </RootStack.Navigator>
  );
};

const styles = StyleSheet.create({
  blocked: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.bg,
  },
  profileButton: {
    marginRight: Platform.OS === 'ios' ? 0 : 8,
  },
});
